// client/src/pages/WatchlistPage.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, CircularProgress, Alert,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import Countdown from '../components/Countdown';
import { API_BASE_URL } from '../utils/media';

const statusColor = { active: 'success', pending: 'warning', ended: 'default', sold: 'info' };

const WatchlistPage = () => {
  const { token } = useContext(AuthContext);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWatchlist = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/watchlist`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch watchlist.');
      setAuctions(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Watchlist</Typography>
      {auctions.length === 0 ? (
        <EmptyState icon={<VisibilityOutlinedIcon />} title="Not watching anything yet" description="Use the watch icon on any auction to track it here without bidding." actionLabel="Browse Auctions" actionTo="/auctions" />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell><TableCell>Current Price</TableCell><TableCell>Ends</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auctions.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell><Link to={`/auctions/${a.id}`}>{a.product_name}</Link></TableCell>
                  <TableCell>{formatCurrency(a.current_price)}</TableCell>
                  <TableCell>{a.status === 'active' ? <Countdown endTime={a.end_time} /> : new Date(a.end_time).toLocaleString()}</TableCell>
                  <TableCell><Chip label={a.status} size="small" color={statusColor[a.status] || 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WatchlistPage;
